<template>
  <div>
    <div class="row px-3 py-2">
      <div class="col-12">
        <h2 class="display-5 mb-3 search-modal-title"><strong>Explore HiiKER</strong></h2>
        <div class="input-group hero-search py-2">
          <input type="text" v-model="search" class="form-control search-bar__input ui-autocomplete-input" placeholder="Search for Hikes" id="search" @keydown.enter="onSearchSubmit">
          <span class="input-group-append d-flex align-items-center">
              <span class="input-group-text">
                <span v-if="isSearching" class="material-icons spinner">autorenew</span>
                <span v-else class="material-icons">search</span>
              </span>
            </span>
        </div>
      </div>
    </div>

    <!-- Recent Searches -->
    <div v-if="recentSearches.length > 0" class="recent-searches-container px-3">
      <div class="recent-searches d-flex align-items-center gap-2">
        <div class="recent-pills d-flex flex-wrap gap-1 flex-grow-1">
          <button
            v-for="(term, index) in recentSearches"
            :key="index"
            class="recent-pill"
            @click="applyRecentSearch(term)"
          >
            <span class="recent-pill-text">{{ term }}</span>
            <span
              class="recent-pill-remove material-icons"
              @click.stop="removeRecentSearch(index)"
            >close</span>
          </button>
        </div>
        <button
          class="recent-clear-btn"
          @click="clearRecentSearches"
          title="Clear all recent searches"
        >
          Clear
        </button>
      </div>
    </div>

    <!-- Mini Tabs Navigation -->
    <div v-if="hasAnyResults" class="mini-tabs-container">
      <div class="mini-tabs d-flex gap-2">
        <button
          v-if="coordinateResult"
          class="mini-tab"
          :class="{ active: activeSection === 'coordinates' }"
          @click="scrollToSection('coordinates')"
        >
          Coordinates
        </button>
        <button
          v-if="searchResults.length > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'popular' }"
          @click="scrollToSection('popular')"
        >
          {{ isShowingNearbyResults ? 'Nearby' : 'Popular' }}
        </button>
        <button
          v-if="userHikesCount > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'hikes' }"
          @click="scrollToSection('hikes')"
        >
          My Planned Hikes
        </button>
        <button
          v-if="userRecordingsCount > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'recordings' }"
          @click="scrollToSection('recordings')"
        >
          Recordings
        </button>
        <button
          v-if="communityHikes.length > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'communityhikes' }"
          @click="scrollToSection('communityhikes')"
        >
          Community Hikes
        </button>
        <button
          v-if="communityRecordings.length > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'communityrecordings' }"
          @click="scrollToSection('communityrecordings')"
        >
          Community Recordings
        </button>
        <button
          v-if="pois.length > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'pois' }"
          @click="scrollToSection('pois')"
        >
          POIs
        </button>
        <button
          v-if="hikeLists.length > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'hikelists' }"
          @click="scrollToSection('hikelists')"
        >
          Hike Lists
        </button>
        <button
          v-if="filteredShortcuts.length > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'shortcuts' }"
          @click="scrollToSection('shortcuts')"
        >
          Shortcuts
        </button>
        <button
          v-if="locationResults.length > 0"
          class="mini-tab"
          :class="{ active: activeSection === 'locations' }"
          @click="scrollToSection('locations')"
        >
          Locations
        </button>
      </div>
    </div>

    <!-- Compact Vertical Sections Layout -->
    <div v-if="hasAnyResults" class="px-3 search-results-sections">

      <!-- Coordinates Section -->
      <div v-if="coordinateResult" class="search-section" ref="coordinatesSection" data-section="coordinates">
        <div class="section-header d-flex gap-2 align-items-center mt-1">
          <h6 class="section-title mb-0 text-muted">COORDINATES</h6>
          <span class="section-count text-muted">1</span>
        </div>
        <div class="section-results">
          <div class="result-item">
            <a
              :href="`/map?lat=${coordinateResult.lat}&lng=${coordinateResult.lng}`"
              class="result-link"
              @click.prevent="navigateToCoordinate(coordinateResult)"
            >
              <div class="result-content d-flex align-items-center">
                <div class="result-thumbnail rounded d-flex align-items-center justify-content-center bg-dark">
                  <span class="material-icons" style="font-size: 20px; color: rgba(255, 255, 255, 0.6);">my_location</span>
                </div>
                <div class="result-info flex-grow-1">
                  <span class="result-name">{{ coordinateResult.displayName }}</span>
                  <div class="result-stats text-muted" style="opacity: 0.7; font-size: 0.75rem;">
                    {{ coordinateResult.format }}
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Nearby/Popular Results Section -->
      <div v-if="searchResults.length > 0" class="search-section" ref="popularSection" data-section="popular">
        <div class="section-header d-flex gap-2 justify-content-between align-items-center mt-1">
          <span class="d-flex align-items-center gap-2">
            <h6 class="section-title mb-0 text-muted">{{ isShowingNearbyResults ? 'NEARBY' : 'POPULAR' }}</h6>
            <span class="section-count text-muted">{{ searchResults.length }}</span>
          </span>
          <button
            class="btn btn-link btn-sm p-0 view-all-btn"
            type="button"
            @click="navigateToExplore"
          >
            View map
          </button>
        </div>
        <div class="section-results">
          <div
            v-for="(result, index) in limitedSearchResults"
            :key="result.document.id"
            class="result-item"
          >
            <a :href="result.document.url" class="result-link" @click="onResultClick(result.document.name, { docId: result.document.id, type: result.document.type, position: index, source: 'popular' })">
              <div class="result-content d-flex align-items-center">
                <img
                  v-if="result.document.type === 'Hike'"
                  class="result-thumbnail rounded"
                  :src="result.document.thumbnail_url"
                  loading="lazy"
                  alt=""
                >
                <img v-else-if="result.document.type === 'Region'" class="result-thumbnail rounded" src="../../images/result_region.webp" loading="lazy" alt="">
                <img v-else-if="result.document.type === 'Country'" class="result-thumbnail rounded" src="../../images/result_country.webp" loading="lazy" alt="">

                <div class="result-info result-info--stacked flex-grow-1">
                  <div class="result-name">{{ result.document.name }}</div>
                  <div v-if="result.document.short_address" class="result-location result-location--block text-muted">{{ result.document.short_address }}</div>
                  <!-- Info row: rating · difficulty · distance — same layout as
                       Android `SearchHikeInfoRow` and iOS `SearchResultRowView`.
                       Only Hike documents carry these fields; Region/Country
                       results skip the row entirely. -->
                  <div
                    v-if="result.document.type === 'Hike' && hasHikeInfoRow(result.document)"
                    class="result-stats result-info-row"
                  >
                    <template v-if="result.document.average_rating > 0">
                      <svg class="info-row-star" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M8 12.146l-4.702 2.472.898-5.236L.39 5.674l5.256-.764L8 .146l2.354 4.764 5.256.764-3.806 3.708.898 5.236z"/>
                      </svg>
                      <span>{{ result.document.average_rating.toFixed(1) }}</span>
                      <span
                        v-if="(result.document.comments_count || result.document.ratings_count) > 0"
                        class="info-row-count"
                      >({{ result.document.comments_count || result.document.ratings_count }})</span>
                    </template>
                    <span
                      v-if="result.document.difficulty"
                      class="info-row-sep"
                      :class="{ 'info-row-sep--first': !(result.document.average_rating > 0) }"
                    >·</span>
                    <span
                      v-if="result.document.difficulty"
                      :style="{ color: difficultyColor(result.document.difficulty) }"
                    >{{ result.document.difficulty }}</span>
                    <span
                      v-if="result.document.distance_meters"
                      class="info-row-sep"
                      :class="{ 'info-row-sep--first': !(result.document.average_rating > 0) && !result.document.difficulty }"
                    >·</span>
                    <span v-if="result.document.distance_meters">{{ formatDistance(result.document.distance_meters) }}</span>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
        <div v-if="hasMorePopularResults" class="show-more-container">
          <button
            class="btn btn-link btn-sm show-more-btn"
            type="button"
            @click="loadMorePopularResults"
          >
            Show more results
          </button>
        </div>
      </div>

      <!-- My Hikes Section -->
      <div v-if="userHikesCount > 0" class="search-section" ref="hikesSection" data-section="hikes">
        <div class="section-header d-flex gap-2 justify-content-between align-items-center mt-1">
          <span class="d-flex align-items-center gap-2">
            <h6 class="section-title mb-0 text-muted">MY PLANNED HIKES</h6>
            <span class="section-count text-muted">{{ userHikesCount }}</span>
          </span>
          <button
            v-if="userHikesCount > maxUserHikesItems"
            class="btn btn-link btn-sm p-0 view-all-btn"
            type="button"
            @click="navigateToUserHikes"
          >
            View all
          </button>
        </div>
        <div class="section-results">
          <div v-for="(hike, index) in limitedUserHikes" :key="hike.id" class="result-item">
            <a :href="hike.shareableUrl" class="result-link" @click="onResultClick(hike.name, { docId: String(hike.id), type: 'Hike', position: index, source: 'my_hikes' })">
              <div class="result-content d-flex align-items-center">
                <img class="result-thumbnail rounded" :src="hike.staticMapImageUrl" loading="lazy" alt="">
                <div class="result-info result-info--stacked flex-grow-1">
                  <div class="result-name">{{ hike.name }}</div>
                  <div v-if="hike.mainRegion?.name || hike.mainCountry?.name" class="result-location result-location--block text-muted">
                    {{ hike.mainRegion?.name }}{{ hike.mainRegion?.name && hike.mainCountry?.name ? ', ' : '' }}{{ hike.mainCountry?.name }}
                  </div>
                  <!-- Info row: rating · difficulty · distance — same treatment as the Popular section. -->
                  <div
                    v-if="(hike.averageRating > 0) || hike.difficulty || hike.distanceMeters"
                    class="result-stats result-info-row"
                  >
                    <template v-if="hike.averageRating > 0">
                      <svg class="info-row-star" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M8 12.146l-4.702 2.472.898-5.236L.39 5.674l5.256-.764L8 .146l2.354 4.764 5.256.764-3.806 3.708.898 5.236z"/>
                      </svg>
                      <span>{{ hike.averageRating.toFixed(1) }}</span>
                      <span
                        v-if="(hike.commentsCount || hike.ratingsCount) > 0"
                        class="info-row-count"
                      >({{ hike.commentsCount || hike.ratingsCount }})</span>
                    </template>
                    <span
                      v-if="hike.difficulty"
                      class="info-row-sep"
                      :class="{ 'info-row-sep--first': !(hike.averageRating > 0) }"
                    >·</span>
                    <span
                      v-if="hike.difficulty"
                      :style="{ color: difficultyColor(hike.difficulty) }"
                    >{{ hike.difficulty }}</span>
                    <span
                      v-if="hike.distanceMeters"
                      class="info-row-sep"
                      :class="{ 'info-row-sep--first': !(hike.averageRating > 0) && !hike.difficulty }"
                    >·</span>
                    <span v-if="hike.distanceMeters">{{ formatDistance(hike.distanceMeters) }}</span>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Recordings Section -->
      <div v-if="userRecordingsCount > 0" class="search-section" ref="recordingsSection" data-section="recordings">
        <div class="section-header d-flex gap-2 align-items-center justify-content-between mt-1">
          <span class="d-flex align-items-center gap-2">
            <h6 class="section-title mb-0 text-muted">RECORDINGS</h6>
            <span class="section-count text-muted">{{ userRecordingsCount }}</span>
          </span>
          <button
            v-if="userRecordingsCount > maxUserRecordingsItems"
            class="btn btn-link btn-sm p-0 view-all-btn"
            type="button"
            @click="navigateToUserRecordings"
          >
            View all
          </button>
        </div>
        <div class="section-results">
          <div v-for="(recording, index) in limitedUserRecordings" :key="recording.id" class="result-item">
            <a :href="recording.shareableUrl" class="result-link" @click="onResultClick(recording.title || 'Untitled Recording', { docId: String(recording.id), type: 'Recording', position: index, source: 'recordings' })">
              <div class="result-content d-flex align-items-center">
                <img class="result-thumbnail rounded" :src="recording.thumbnailUrl" loading="lazy" alt="">
                <div class="result-info flex-grow-1">
                  <div class="d-flex align-items-baseline justify-content-between">
                    <span class="result-name">{{ recording.title || 'Untitled Recording' }}</span>
                  </div>
                  <div v-if="recording.distanceMeters" class="result-stats text-yellow">
                    {{ formatDistance(recording.distanceMeters) }}
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Community Hikes Section (public hikes from other users — parity with iOS/Android) -->
      <div v-if="communityHikes.length > 0" class="search-section" ref="communityHikesSection" data-section="communityhikes">
        <div class="section-header d-flex gap-2 align-items-center mt-1">
          <h6 class="section-title mb-0 text-muted">COMMUNITY HIKES</h6>
          <span class="section-count text-muted">{{ communityHikes.length }}</span>
        </div>
        <div class="section-results">
          <div v-for="(item, index) in limitedCommunityHikes" :key="item.id" class="result-item">
            <a :href="item.itemUrl" class="result-link" @click="onResultClick(item.title, { docId: item.id, type: 'CommunityHike', position: index, source: 'community_hikes' })">
              <div class="result-content d-flex align-items-center">
                <img v-if="item.staticMap" class="result-thumbnail rounded" :src="item.staticMap" loading="lazy" alt="">
                <div v-else class="result-thumbnail rounded d-flex align-items-center justify-content-center bg-dark">
                  <span class="material-icons" style="font-size: 20px; color: rgba(255, 255, 255, 0.6);">terrain</span>
                </div>
                <div class="result-info flex-grow-1">
                  <div class="d-flex align-items-baseline justify-content-between">
                    <span class="result-name">{{ item.title }}</span>
                    <span v-if="item.location" class="result-location text-muted ms-2">{{ item.location }}</span>
                  </div>
                  <div class="result-stats text-muted" style="opacity: 0.7; font-size: 0.75rem;">
                    <span v-if="item.userDisplayName">{{ item.userDisplayName }}</span>
                    <span v-if="item.userDisplayName && item.distanceMeters"> · </span>
                    <span v-if="item.distanceMeters">{{ formatDistance(item.distanceMeters) }}</span>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Community Recordings Section (public recordings from other users) -->
      <div v-if="communityRecordings.length > 0" class="search-section" ref="communityRecordingsSection" data-section="communityrecordings">
        <div class="section-header d-flex gap-2 align-items-center mt-1">
          <h6 class="section-title mb-0 text-muted">COMMUNITY RECORDINGS</h6>
          <span class="section-count text-muted">{{ communityRecordings.length }}</span>
        </div>
        <div class="section-results">
          <div v-for="(item, index) in limitedCommunityRecordings" :key="item.id" class="result-item">
            <a :href="item.itemUrl" class="result-link" @click="onResultClick(item.title, { docId: item.id, type: 'CommunityRecording', position: index, source: 'community_recordings' })">
              <div class="result-content d-flex align-items-center">
                <img v-if="item.staticMap" class="result-thumbnail rounded" :src="item.staticMap" loading="lazy" alt="">
                <div v-else class="result-thumbnail rounded d-flex align-items-center justify-content-center bg-dark">
                  <span class="material-icons" style="font-size: 20px; color: rgba(255, 255, 255, 0.6);">timeline</span>
                </div>
                <div class="result-info flex-grow-1">
                  <div class="d-flex align-items-baseline justify-content-between">
                    <span class="result-name">{{ item.title }}</span>
                    <span v-if="item.location" class="result-location text-muted ms-2">{{ item.location }}</span>
                  </div>
                  <div class="result-stats text-muted" style="opacity: 0.7; font-size: 0.75rem;">
                    <span v-if="item.userDisplayName">{{ item.userDisplayName }}</span>
                    <span v-if="item.userDisplayName && item.distanceMeters"> · </span>
                    <span v-if="item.distanceMeters">{{ formatDistance(item.distanceMeters) }}</span>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- POIs Section -->
      <div v-if="pois.length > 0" class="search-section" ref="poisSection" data-section="pois">
        <div class="section-header d-flex gap-2 align-items-center mt-1">
          <h6 class="section-title mb-0 text-muted">POINTS OF INTEREST</h6>
          <span class="section-count text-muted">{{ pois.length }}</span>
        </div>
        <div class="section-results">
          <div v-for="(poi, index) in limitedPois" :key="poi.id" class="result-item">
            <a :href="poi.url" class="result-link" @click="onResultClick(poi.name, { docId: String(poi.id), type: 'POI', position: index, source: 'pois' })">
              <div class="result-content d-flex align-items-center">
                <div class="result-thumbnail rounded d-flex align-items-center justify-content-center bg-dark">
                  <span class="material-icons" style="font-size: 20px; color: rgba(255, 255, 255, 0.6);">place</span>
                </div>
                <div class="result-info flex-grow-1">
                  <div class="d-flex align-items-baseline justify-content-between">
                    <span class="result-name">{{ poi.name }}</span>
                    <span v-if="poi.location" class="result-location text-muted ms-2">{{ poi.location }}</span>
                  </div>
                  <div v-if="poi.type" class="result-stats text-yellow">
                    {{ poi.type }}
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>


      <!-- Hike Lists Section -->
      <div v-if="hikeLists.length > 0" class="search-section" ref="hikeListsSection" data-section="hikelists">
        <div class="section-header d-flex gap-2 justify-content-between align-items-center mt-1">
          <span class="d-flex align-items-center gap-2">
            <h6 class="section-title mb-0 text-muted">HIKE LISTS</h6>
            <span class="section-count text-muted">{{ hikeLists.length }}</span>
          </span>
          <button
            v-if="hikeLists.length > maxHikeListsItems"
            class="btn btn-link btn-sm p-0 view-all-btn"
            type="button"
            @click="navigateToUserHikeLists"
          >
            View all
          </button>
        </div>
        <div class="section-results">
          <div v-for="(list, index) in limitedHikeLists" :key="list.id" class="result-item">
            <a :href="list.shareableUrl" class="result-link" @click="onResultClick(list.name, { docId: String(list.id), type: 'HikeList', position: index, source: 'hikelists' })">
              <div class="result-content d-flex align-items-center">
                <img v-if="list.images && list.images.length > 0 && list.images[0].file" class="result-thumbnail rounded" :src="list.images[0].file.smUrl" loading="lazy" alt="">
                <div v-else class="result-thumbnail rounded d-flex align-items-center justify-content-center bg-dark">
                  <span class="material-icons" style="font-size: 20px; color: rgba(255, 255, 255, 0.6);">list</span>
                </div>
                <div class="result-info flex-grow-1">
                  <div class="d-flex align-items-baseline justify-content-between">
                    <span class="result-name">{{ list.name }}</span>
                  </div>
                  <div v-if="list.hikesCount" class="result-stats text-yellow">
                    {{ list.hikesCount }} {{ list.hikesCount === 1 ? 'hike' : 'hikes' }}
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Shortcuts Section - Shows at the bottom -->
      <div v-if="filteredShortcuts.length > 0" class="search-section" ref="shortcutsSection" data-section="shortcuts">
        <div class="section-header d-flex gap-2 align-items-center mt-1">
          <h6 class="section-title mb-0 text-muted">SHORTCUTS</h6>
          <span class="section-count text-muted">{{ filteredShortcuts.length }}</span>
        </div>
        <div class="section-results">
          <div v-for="(shortcut, index) in limitedShortcuts" :key="shortcut.id" class="result-item">
            <a :href="shortcut.url" class="result-link">
              <div class="result-content d-flex align-items-center">
                <div class="result-thumbnail rounded d-flex align-items-center justify-content-center bg-dark">
                  <span class="material-icons" style="font-size: 20px; color: rgba(255, 255, 255, 0.6);">{{ shortcut.icon }}</span>
                </div>
                <div class="result-info flex-grow-1">
                  <div class="d-flex align-items-baseline justify-content-between">
                    <span class="result-name">{{ shortcut.name }}</span>
                  </div>
                  <div v-if="shortcut.description" class="result-stats text-muted" style="opacity: 0.7; font-size: 0.75rem;">
                    {{ shortcut.description }}
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Locations Section (Stadia Geocoding Results) -->
      <div v-if="locationResults.length > 0" class="search-section" ref="locationsSection" data-section="locations">
        <div class="section-header d-flex gap-2 justify-content-between align-items-center mt-1">
          <span class="d-flex align-items-center gap-2">
            <h6 class="section-title mb-0 text-muted">LOCATIONS</h6>
            <span class="section-count text-muted">{{ locationResults.length }}</span>
          </span>
        </div>
        <div class="section-results">
          <div
            v-for="(location, index) in limitedLocationResults"
            :key="location.place_id"
            class="result-item"
          >
            <a :href="`/explore?lat=${location.lat}&lng=${location.lon}&zoom=12`" class="result-link" @click="onResultClick(location.display_name, { docId: String(location.place_id), type: 'Location', position: index, source: 'locations' })">
              <div class="result-content d-flex align-items-center">
                <div class="result-thumbnail rounded d-flex align-items-center justify-content-center bg-dark">
                  <span class="material-icons" style="font-size: 20px; color: rgba(255, 255, 255, 0.6);">place</span>
                </div>
                <div class="result-info flex-grow-1">
                  <div class="d-flex align-items-baseline justify-content-between">
                    <span class="result-name">{{ location.display_name }}</span>
                  </div>
                  <div v-if="location.type" class="result-stats text-muted" style="opacity: 0.7; font-size: 0.75rem;">
                    {{ location.type }}
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

    </div>

    <div v-else-if="showEmptyState" class="px-3 search-empty-state">
      <div class="empty-message text-muted">
        No search results found, try again with a different query.
      </div>
      <div v-if="fallbackShortcuts.length > 0" class="search-section">
        <div class="section-header d-flex gap-2 align-items-center mt-1">
          <h6 class="section-title mb-0 text-muted">SHORTCUTS</h6>
          <span class="section-count text-muted">{{ fallbackShortcuts.length }}</span>
        </div>
        <div class="section-results">
          <div v-for="shortcut in fallbackShortcuts" :key="shortcut.id" class="result-item">
            <a :href="shortcut.url" class="result-link">
              <div class="result-content d-flex align-items-center">
                <div class="result-thumbnail rounded d-flex align-items-center justify-content-center bg-dark">
                  <span class="material-icons" style="font-size: 20px; color: rgba(255, 255, 255, 0.6);">{{ shortcut.icon }}</span>
                </div>
                <div class="result-info flex-grow-1">
                  <div class="d-flex align-items-baseline justify-content-between">
                    <span class="result-name">{{ shortcut.name }}</span>
                  </div>
                  <div v-if="shortcut.description" class="result-stats text-muted" style="opacity: 0.7; font-size: 0.75rem;">
                    {{ shortcut.description }}
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, onUnmounted, computed } from "vue";
import { TypesenseSearchable } from "../../utils/models/TypesenseSearchable";
import graph_client from "../../utils/graphql/graph_client";
import { SearchUserHikesSimple, SearchUserHikesCount } from "../../utils/graphql/hikes-graph";
import { SearchUserRecordingsSimple, SearchUserRecordingsCount } from "../../utils/graphql/recorded-trail-graph";
import { SearchUserHikeLists } from "../../utils/graphql/hike-list-graph";
import { MeasurementPrefUtility } from "../../utils/helpers/MeasurementPref";
import { HikeTimeUtility, PrettyTime } from "../../utils/helpers/HikeTime";
import { searchShortcuts, SEARCH_SHORTCUTS, type SearchShortcut } from "../../utils/data/SearchShortcuts";
import { stadiaGeocodingService, type StadiaGeocodingResult } from "../../utils/services/StadiaGeocodingService";
import { searchCommunityFeed, type CommunityFeedSearchItem } from "../../utils/helpers/CommunityFeedSearch";
import { ipGeoLocationService } from "../../utils/services/IpGeoLocationService";
import { buildSearchableSortBy } from "../../utils/buildSearchableSortBy";
import emitter from "../../utils/bus";
import { SearchAnalytics, type SearchClickPayload } from "./analytics/SearchAnalytics";
import { parseCoordinate, type ParsedCoordinate } from "../../utils/helpers/CoordinateParser";
import { difficultyColor } from "../../utils/helpers/difficulty";

export default defineComponent({
  props: {
    excludeTypes: {
      type: Array as () => string[],
      default: () => []
    }
  },
  setup(props) {
    const city = ref("");
    const region = ref("");
    const search = ref("");
    const searchResults = ref<any[]>([]);
    const userHikes = ref<any[]>([]);
    const userRecordings = ref<any[]>([]);
    const userHikesCount = ref<number>(0);
    const userRecordingsCount = ref<number>(0);
    const pois = ref<any[]>([]);
    const shortcuts = ref<any[]>([]);
    const hikeLists = ref<any[]>([]);
    const locationResults = ref<StadiaGeocodingResult[]>([]);
    // Community feed search results (public hikes & recordings from other
    // users), matching iOS/Android. Split by item_type like the apps do.
    const communityHikes = ref<CommunityFeedSearchItem[]>([]);
    const communityRecordings = ref<CommunityFeedSearchItem[]>([]);
    const coordinateResult = ref<ParsedCoordinate | null>(null);
    const fallbackShortcuts = ref<SearchShortcut[]>([]);
    const fallbackShortcutIds = [1, 2, 7];

    // Recent searches
    const RECENT_SEARCHES_KEY = 'hiiker_recent_searches';
    const MAX_RECENT_SEARCHES = 7;
    const MIN_SEARCH_LENGTH = 1; // Minimum characters required to trigger search
    const recentSearches = ref<string[]>([]);

    const loadRecentSearches = () => {
      try {
        const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
        if (stored) {
          recentSearches.value = JSON.parse(stored);
        }
      } catch (e) {
        console.error('Error loading recent searches:', e);
        recentSearches.value = [];
      }
    };

    const saveRecentSearches = () => {
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches.value));
      } catch (e) {
        console.error('Error saving recent searches:', e);
      }
    };

    const addRecentSearch = (term: string) => {
      const trimmed = term.trim();
      if (!trimmed || trimmed.length < MIN_SEARCH_LENGTH) return;

      // Remove if already exists (to move to front)
      const existingIndex = recentSearches.value.findIndex(
        s => s.toLowerCase() === trimmed.toLowerCase()
      );
      if (existingIndex !== -1) {
        recentSearches.value.splice(existingIndex, 1);
      }

      // Add to front
      recentSearches.value.unshift(trimmed);

      // Keep only max items
      if (recentSearches.value.length > MAX_RECENT_SEARCHES) {
        recentSearches.value = recentSearches.value.slice(0, MAX_RECENT_SEARCHES);
      }

      saveRecentSearches();
    };

    const removeRecentSearch = (index: number) => {
      recentSearches.value.splice(index, 1);
      saveRecentSearches();
    };

    const clearRecentSearches = () => {
      recentSearches.value = [];
      saveRecentSearches();
    };

    const applyRecentSearch = (term: string) => {
      search.value = term;
    };

    const onSearchSubmit = () => {
      if (search.value.trim().length >= MIN_SEARCH_LENGTH) {
        addRecentSearch(search.value);
      }
    };

    const onResultClick = (name: string, click?: SearchClickPayload) => {
      if (name && name.trim().length >= MIN_SEARCH_LENGTH) {
        addRecentSearch(name.trim());
      }
      if (click) SearchAnalytics.recordClick(click);
    };
    fallbackShortcuts.value = SEARCH_SHORTCUTS.filter(shortcut => fallbackShortcutIds.includes(shortcut.id));
    const isLoggedIn = computed(() => Boolean(window.user));
    const activeSection = ref<'popular' | 'hikes' | 'recordings' | 'communityhikes' | 'communityrecordings' | 'pois' | 'shortcuts' | 'hikelists' | 'locations' | 'coordinates'>('popular');
    const isSearching = ref(false);

    // Section refs
    const popularSection = ref<HTMLElement | null>(null);
    const hikesSection = ref<HTMLElement | null>(null);
    const recordingsSection = ref<HTMLElement | null>(null);
    const communityHikesSection = ref<HTMLElement | null>(null);
    const communityRecordingsSection = ref<HTMLElement | null>(null);
    const poisSection = ref<HTMLElement | null>(null);
    const shortcutsSection = ref<HTMLElement | null>(null);
    const hikeListsSection = ref<HTMLElement | null>(null);
    const locationsSection = ref<HTMLElement | null>(null);
    const coordinatesSection = ref<HTMLElement | null>(null);

    // Define allowed types (Trail = official HiiKER trails, Hike = user-created hikes)
    const allowedTypes = ["Hike", "Region", "Country", "POI"];

    // Store user's geo location for sorting nearby hikes
    const userGeoLocation = ref<{ lat: number; lng: number } | null>(null);

    const fetchUserLists = async (query = '') => {
      if (!window.user) {
        userHikes.value = [];
        userRecordings.value = [];
        userHikesCount.value = 0;
        userRecordingsCount.value = 0;
        hikeLists.value = [];
        return;
      }

      // Fetch counts and limited data in parallel for better performance
      const promises = [];

      // Fetch hikes count and limited data
      promises.push(
        graph_client.request(SearchUserHikesCount, { query })
          .then(response => {
            userHikesCount.value = response.searchUserHikesCount || 0;
          })
          .catch(error => {
            console.error('Error fetching user hikes count:', error);
            userHikesCount.value = 0;
          })
      );

      promises.push(
        graph_client.request(SearchUserHikesSimple, {
          query,
          page: 1,
          perPage: maxUserHikesItems,
          // Global search should surface all of the user's hikes, including completed.
          hideCompletedHikes: false
        })
          .then(response => {
            userHikes.value = response.searchUserHikes || [];
          })
          .catch(error => {
            console.error('Error searching user hikes:', error);
            userHikes.value = [];
          })
      );

      // Fetch recordings count and limited data
      promises.push(
        graph_client.request(SearchUserRecordingsCount, { query })
          .then(response => {
            userRecordingsCount.value = response.searchUserRecordingsCount || 0;
          })
          .catch(error => {
            console.error('Error fetching user recordings count:', error);
            userRecordingsCount.value = 0;
          })
      );

      promises.push(
        graph_client.request(SearchUserRecordingsSimple, {
          query,
          page: 1,
          perPage: maxUserRecordingsItems
        })
          .then(response => {
            userRecordings.value = response.searchUserRecordings || [];
          })
          .catch(error => {
            console.error('Error searching user recordings:', error);
            userRecordings.value = [];
          })
      );

      // Fetch hike lists (still fetching all as before)
      promises.push(
        graph_client.request(SearchUserHikeLists, {
          query,
          page: 1,
          perPage: 50
        })
          .then(response => {
            hikeLists.value = response.searchUserHikeLists || [];
            console.log(`Hike lists search for "${query}": ${hikeLists.value.length} results`);
          })
          .catch(error => {
            console.error('Error searching user hike lists:', error);
            if (error.response) {
              console.error('GraphQL errors:', error.response.errors);
            }
            hikeLists.value = [];
          })
      );

      await Promise.all(promises);
    };

    const onStringChange = async (newVal: string) => {
      isSearching.value = true;

      try {
        // Search shortcuts first (synchronous, no waiting needed)
        shortcuts.value = searchShortcuts(newVal);

        // Coordinate detection — synchronous, always runs regardless of length
        coordinateResult.value = parseCoordinate(newVal);

        // If query is too short, return to initial state instead of blank
        if (newVal.trim().length < MIN_SEARCH_LENGTH) {
          coordinateResult.value = null;
          SearchAnalytics.setQuery("");
          await fetchInitialResults();
          isSearching.value = false;
          return;
        }

        SearchAnalytics.setQuery(newVal);

        // Create promises for all async searches
        const searches: Promise<void>[] = [];

        // Sort: text match → soft distance bands (if location known) → popularity.
        // See buildSearchableSortBy for the rationale (coarse _eval tiers keep
        // distance a soft signal so popularity orders within each tier).
        const searchSortBy = buildSearchableSortBy(userGeoLocation.value);

        // Search hikes/trails/POIs with faceting by type
        const typesenseSearch = TypesenseSearchable.get(
          newVal,
          200,
          allowedTypes,
          props.excludeTypes,
          ['type'], // Facet by type to get counts for each type
          100, // Max facet values
          searchSortBy,
          null  // geo is already embedded in searchSortBy
        ).then((response: any) => {
          if (response && response.hits) {
            // Separate POIs from other results
            const allResults = response.hits;
            pois.value = allResults.filter((hit: any) => hit.document.type === 'POI').map((hit: any) => ({
              id: hit.document.id,
              name: hit.document.name,
              location: hit.document.short_address,
              type: hit.document.region_name || 'Point of Interest',
              url: hit.document.url
            }));

            // Keep hikes, trails, regions, countries in main results
            searchResults.value = allResults.filter((hit: any) =>
              ['Hike', 'Region', 'Country'].includes(hit.document.type)
            );

            // Log facet counts for debugging
            if (response.facet_counts && response.facet_counts.length > 0) {
              console.log('Search facets:', response.getFacetCounts('type'));
            }
          }
        });
        searches.push(typesenseSearch);

        // Search user lists if logged in
        if (isLoggedIn.value) {
          searches.push(fetchUserLists(newVal || ''));
        } else {
          userHikes.value = [];
          userRecordings.value = [];
          userHikesCount.value = 0;
          userRecordingsCount.value = 0;
        }

        // Search Stadia for address/location results
        const locationSearch = stadiaGeocodingService.searchAddress(newVal).then((results) => {
          locationResults.value = results;
        }).catch((error) => {
          console.error('Stadia geocoding search failed:', error);
          locationResults.value = [];
        });
        searches.push(locationSearch);

        // Search the community feed (public hikes & recordings from other
        // users), matching iOS/Android. Split by item_type for display.
        const communitySearch = searchCommunityFeed(newVal).then((items) => {
          communityHikes.value = items.filter((item) => item.itemType === 'Hike');
          communityRecordings.value = items.filter((item) => item.itemType === 'Recording');
        });
        searches.push(communitySearch);

        // Wait for all searches to complete
        await Promise.all(searches);
      } finally {
        isSearching.value = false;
      }
    };

    // Debounce utility
    let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    const debouncedSearch = (value: string) => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      // Show loading immediately
      isSearching.value = true;

      debounceTimeout = setTimeout(() => {
        onStringChange(value);
      }, 300); // 300ms debounce delay
    };

    watch(search, (newVal) => {
      debouncedSearch(newVal);
      // Reset popular results pagination when search changes
      popularResultsShown.value = maxPopularItems;
    });


    const maxPopularItems = 8;
    const maxUserHikesItems = 3;
    const maxUserRecordingsItems = 3;
    const maxCommunityItems = 3;
    const maxPoisItems = 3;
    const maxShortcutsItems = 7; // Show all shortcuts if they match
    const maxHikeListsItems = 3;
    const maxLocationItems = 5;

    // Popular results pagination
    const popularResultsShown = ref(maxPopularItems);
    const popularResultsIncrement = 8;

    const limitedSearchResults = computed(() => searchResults.value.slice(0, popularResultsShown.value));
    const limitedUserHikes = computed(() => userHikes.value.slice(0, maxUserHikesItems));
    const limitedUserRecordings = computed(() => userRecordings.value.slice(0, maxUserRecordingsItems));
    const limitedCommunityHikes = computed(() => communityHikes.value.slice(0, maxCommunityItems));
    const limitedCommunityRecordings = computed(() => communityRecordings.value.slice(0, maxCommunityItems));
    const limitedPois = computed(() => pois.value.slice(0, maxPoisItems));
    const limitedHikeLists = computed(() => hikeLists.value.slice(0, maxHikeListsItems));
    const limitedLocationResults = computed(() => locationResults.value.slice(0, maxLocationItems));

    // Filter shortcuts based on login status
    // Logged out users: only show IDs 1, 2, 7 (Create Hike, Search Map, Help Center)
    // Logged in users: show all shortcuts
    const filteredShortcuts = computed(() => {
      if (isLoggedIn.value) {
        return shortcuts.value;
      } else {
        // Only show non-user-content shortcuts when logged out
        return shortcuts.value.filter(s => fallbackShortcutIds.includes(s.id));
      }
    });

    const limitedShortcuts = computed(() => filteredShortcuts.value.slice(0, maxShortcutsItems));

    // Check if there are more popular results to show
    const hasMorePopularResults = computed(() => searchResults.value.length > popularResultsShown.value);

    // Function to load more popular results
    const loadMorePopularResults = () => {
      popularResultsShown.value += popularResultsIncrement;
    };

    const hasAnyResults = computed(() =>
      searchResults.value.length > 0 ||
      userHikesCount.value > 0 ||
      userRecordingsCount.value > 0 ||
      communityHikes.value.length > 0 ||
      communityRecordings.value.length > 0 ||
      pois.value.length > 0 ||
      hikeLists.value.length > 0 ||
      filteredShortcuts.value.length > 0 ||
      locationResults.value.length > 0 ||
      coordinateResult.value !== null
    );

    const showEmptyState = computed(() => !hasAnyResults.value && search.value.trim().length >= MIN_SEARCH_LENGTH);

    // Show "Nearby" instead of "Popular" when using geo-sorted results and not actively searching
    const isShowingNearbyResults = computed(() =>
      userGeoLocation.value !== null && search.value.trim().length < MIN_SEARCH_LENGTH
    );

    const fetchInitialResults = async () => {
      // Filter to only show Hikes in initial results (not regions/countries)
      const hikeOnlyTypes = ["Hike"];

      // Use geo-sorting if user location is available
      if (userGeoLocation.value) {
        // Fetch nearby hikes sorted by distance from user
        const response = await TypesenseSearchable.get(
          '*',  // Wildcard query to get all nearby hikes
          250,
          hikeOnlyTypes,
          props.excludeTypes,
          null,
          100,
          null,  // No text-based sorting, use geo
          userGeoLocation.value
        );
        if (response && response.hits.length > 0) {
          searchResults.value = response.hits.slice(0, 8);
        } else {
          // Fallback to popular hikes if no nearby results
          const fallbackResponse = await TypesenseSearchable.get('', 250, hikeOnlyTypes, props.excludeTypes, null, 100, 'popularity:desc');
          searchResults.value = fallbackResponse?.hits.slice(0, 8) || [];
        }
      } else {
        // No geo location available, try city/region name search then fall back to popular
        let response = await TypesenseSearchable.get(city.value || '', 250, hikeOnlyTypes, props.excludeTypes, null, 100, 'popularity:desc');
        if (response && response.hits.length > 0) {
          searchResults.value = response.hits.slice(0, 8);
        } else {
          // Fallback to region
          response = await TypesenseSearchable.get(region.value || '', 250, hikeOnlyTypes, props.excludeTypes, null, 100, 'popularity:desc');
          if (response && response.hits.length > 0) {
            searchResults.value = response.hits.slice(0, 8);
          } else {
            response = await TypesenseSearchable.get('', 250, hikeOnlyTypes, props.excludeTypes, null, 100, 'popularity:desc');
            searchResults.value = response?.hits.slice(0, 8) || [];
          }
        }
      }

      if (isLoggedIn.value) {
        await fetchUserLists('');
      }

      // POIs are now fetched from Typesense search
      pois.value = [];

      // Clear location results in initial state
      locationResults.value = [];

      // Community feed is only shown for active text searches
      communityHikes.value = [];
      communityRecordings.value = [];

      // Shortcuts: Show default shortcuts in initial view for all users
      fallbackShortcuts.value = SEARCH_SHORTCUTS.filter(shortcut => fallbackShortcutIds.includes(shortcut.id));
      if (!isLoggedIn.value) {
        // Logged out: only show non-user-content shortcuts
        shortcuts.value = SEARCH_SHORTCUTS.filter(s => fallbackShortcutIds.includes(s.id));
      } else {
        // Logged in: show all shortcuts
        shortcuts.value = SEARCH_SHORTCUTS;
      }

      // Hike lists are now fetched via GraphQL in fetchUserLists
      // No need to initialize with placeholder data
    };

    // Handle user updates - refresh user-specific content
    const handleUserUpdate = async () => {
      // Refresh user lists when user logs in/out or is updated
      console.log('User updated, refreshing user-specific search results');
      if (window.user) {
        await fetchUserLists(search.value || '');
      } else {
        userHikes.value = [];
        userRecordings.value = [];
        userHikesCount.value = 0;
        userRecordingsCount.value = 0;
        hikeLists.value = [];
      }
    };

    onMounted(async () => {
      // Load recent searches from localStorage
      loadRecentSearches();

      // Fetch user's location for nearby hike sorting
      try {
        const geoLocation = await ipGeoLocationService.getLocation();
        if (geoLocation) {
          userGeoLocation.value = { lat: geoLocation.lat, lng: geoLocation.lng };
          city.value = geoLocation.city || '';
          region.value = geoLocation.region || '';
        }
      } catch (error) {
        console.error('Failed to get user location:', error);
      }

      fetchInitialResults();

      // Add scroll listener for tab navigation
      // Try multiple possible scroll containers
      const modalContent = document.querySelector('#searchModal .modal-content');
      const modalBody = document.querySelector('#searchModal .modal-body');
      const modalDialog = document.querySelector('#searchModal .modal-dialog');

      // Attach to whichever element actually scrolls
      if (modalContent) {
        modalContent.addEventListener('scroll', handleScroll);
        console.log('Attached scroll listener to modal-content');
      }
      if (modalBody) {
        modalBody.addEventListener('scroll', handleScroll);
        console.log('Attached scroll listener to modal-body');
      }
      if (modalDialog) {
        modalDialog.addEventListener('scroll', handleScroll);
        console.log('Attached scroll listener to modal-dialog');
      }

      // Listen for user updates to refresh user options
      emitter.on('user:updated', handleUserUpdate);
    });

    // Formatting methods
    const formatDistance = (distance: number) => {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.bigUnitFormatter(distance / 1000, true);
    };

    const formatElevation = (elevation: number) => {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.smallUnitFormatter(elevation, false);
    };

    const formatDuration = (durationSeconds: number) => {
      const prettyTime = new PrettyTime(durationSeconds).prettyTime();
      return prettyTime;
    };

    // True when a Hike search-result document has at least one field we
    // can show in the rating/difficulty/distance info row. Skipping the
    // whole row when all three fields are absent avoids a stray empty
    // line under the short_address.
    const hasHikeInfoRow = (doc: any) => {
      return (doc?.average_rating > 0)
        || !!doc?.difficulty
        || (doc?.distance_meters > 0);
    };

    const navigateToCoordinate = (coord: ParsedCoordinate) => {
      onResultClick(coord.displayName, { docId: 'coord', type: 'Coordinate', position: 0, source: 'coordinates' });
      if (window.location.pathname === '/map' && (window as any).map) {
        (window as any).map.flyTo({ center: [coord.lng, coord.lat], zoom: 15, essential: true });
        const modalEl = document.getElementById('searchModal');
        if (modalEl) {
          const { Modal } = (window as any).bootstrap || {};
          Modal?.getInstance(modalEl)?.hide();
        }
      } else {
        window.location.href = `/map?lat=${coord.lat}&lng=${coord.lng}`;
      }
    };

    const navigateToExplore = () => {
      window.location.href = '/explore';
    };

    const navigateToUserHikes = () => {
      window.location.href = '/user/hikes';
    };

    const navigateToUserRecordings = () => {
      window.location.href = '/user/recordings';
    };

    const navigateToUserHikeLists = () => {
      window.location.href = '/user/hike-lists';
    };

    // Scroll to section
    const scrollToSection = (section: 'popular' | 'hikes' | 'recordings' | 'communityhikes' | 'communityrecordings' | 'pois' | 'shortcuts' | 'hikelists' | 'coordinates') => {
      const sectionMap: Record<string, HTMLElement | null> = {
        popular: popularSection.value,
        hikes: hikesSection.value,
        recordings: recordingsSection.value,
        communityhikes: communityHikesSection.value,
        communityrecordings: communityRecordingsSection.value,
        pois: poisSection.value,
        shortcuts: shortcutsSection.value,
        hikelists: hikeListsSection.value,
        coordinates: coordinatesSection.value,
      };
      const targetSection = sectionMap[section];
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        activeSection.value = section;
      }
    };

    // Handle scroll to update active section
    const handleScroll = (event: Event) => {
      console.log('Scroll event fired!', event.target);

      // Find the scrolling container
      const scrollContainer = event.target as HTMLElement;
      if (!scrollContainer) return;

      const sections = [
        { ref: coordinatesSection.value, name: 'coordinates' as const },
        { ref: shortcutsSection.value, name: 'shortcuts' as const },
        { ref: popularSection.value, name: 'popular' as const },
        { ref: hikesSection.value, name: 'hikes' as const },
        { ref: recordingsSection.value, name: 'recordings' as const },
        { ref: communityHikesSection.value, name: 'communityhikes' as const },
        { ref: communityRecordingsSection.value, name: 'communityrecordings' as const },
        { ref: poisSection.value, name: 'pois' as const },
        { ref: hikeListsSection.value, name: 'hikelists' as const }
      ].filter(s => s.ref !== null);

      const scrollPosition = scrollContainer.scrollTop;
      const offset = 150; // Offset from top to determine active section

      console.log('Scroll position:', scrollPosition);

      // Find which section is currently in view
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.ref) {
          // Get the position of the section relative to the scroll container
          const rect = section.ref.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const sectionTop = rect.top - containerRect.top + scrollContainer.scrollTop;

          console.log(`Section ${section.name} top:`, sectionTop);

          if (sectionTop <= scrollPosition + offset) {
            activeSection.value = section.name;
            console.log('Active section:', activeSection.value);
            break;
          }
        }
      }
    };


    onUnmounted(() => {
      // Remove scroll listeners from all possible containers
      const modalContent = document.querySelector('#searchModal .modal-content');
      const modalBody = document.querySelector('#searchModal .modal-body');
      const modalDialog = document.querySelector('#searchModal .modal-dialog');

      if (modalContent) {
        modalContent.removeEventListener('scroll', handleScroll);
      }
      if (modalBody) {
        modalBody.removeEventListener('scroll', handleScroll);
      }
      if (modalDialog) {
        modalDialog.removeEventListener('scroll', handleScroll);
      }

      // Remove user update listener
      emitter.off('user:updated', handleUserUpdate);
    });

    return {
      city,
      region,
      search,
      searchResults,
      userHikes,
      userRecordings,
      userHikesCount,
      userRecordingsCount,
      communityHikes,
      communityRecordings,
      pois,
      shortcuts,
      filteredShortcuts,
      hikeLists,
      locationResults,
      hasAnyResults,
      showEmptyState,
      isShowingNearbyResults,
      fetchUserLists,
      isLoggedIn,
      isSearching,
      formatDistance,
      formatElevation,
      formatDuration,
      difficultyColor,
      hasHikeInfoRow,
      onStringChange,
      limitedSearchResults,
      limitedUserHikes,
      limitedUserRecordings,
      limitedCommunityHikes,
      limitedCommunityRecordings,
      limitedPois,
      limitedShortcuts,
      limitedHikeLists,
      limitedLocationResults,
      maxPopularItems,
      maxUserHikesItems,
      maxUserRecordingsItems,
      navigateToCoordinate,
      navigateToExplore,
      navigateToUserHikes,
      navigateToUserRecordings,
      navigateToUserHikeLists,
      activeSection,
      scrollToSection,
      popularSection,
      hikesSection,
      recordingsSection,
      communityHikesSection,
      communityRecordingsSection,
      poisSection,
      shortcutsSection,
      hikeListsSection,
      locationsSection,
      coordinatesSection,
      coordinateResult,
      fallbackShortcuts,
      recentSearches,
      applyRecentSearch,
      removeRecentSearch,
      clearRecentSearches,
      onSearchSubmit,
      onResultClick,
      maxHikeListsItems,
      hasMorePopularResults,
      loadMorePopularResults
    };
  },
});
</script>

<style scoped>
  .modal-content{
    height: 84vh;
  }

  /* Recent Searches */
  .recent-searches-container {
    margin-top: 0.5rem;
    padding-bottom: 0.5rem;
  }

  .recent-searches {
    min-height: 28px;
  }

  .recent-pills {
    overflow: hidden;
  }

  .recent-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    background-color: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 100px;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    max-width: 150px;
  }

  .recent-pill:hover {
    background-color: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .recent-pill-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .recent-pill-remove {
    font-size: 14px;
    opacity: 0.5;
    transition: opacity 0.15s ease;
    margin-left: 0.125rem;
  }

  .recent-pill:hover .recent-pill-remove {
    opacity: 0.8;
  }

  .recent-pill-remove:hover {
    opacity: 1;
    color: #ff6b6b;
  }

  .recent-clear-btn {
    flex-shrink: 0;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    background-color: transparent;
    color: rgba(255, 255, 255, 0.4);
    border: none;
    cursor: pointer;
    transition: color 0.15s ease;
  }

  .recent-clear-btn:hover {
    color: rgba(255, 255, 255, 0.7);
  }

  /* Position title inline with modal close button */
  .search-modal-title {
    width: fit-content;
    margin-top: -3rem;
    padding-top: 0.25rem;
  }

  /* Mini Tabs Navigation */
  .mini-tabs-container {
    margin-top: 0.75rem;
    margin-bottom: 0rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    position: sticky;
    top: 0;
    backdrop-filter: blur(10px);
    z-index: 10;
    padding-top: 0.5rem;
    margin-top: 0.25rem;
  }

  .mini-tabs {
    display: flex;
    gap: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .mini-tabs::-webkit-scrollbar {
    display: none;
  }

  .mini-tab {
    flex-shrink: 0;
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 500;
    background-color: transparent;
    color: rgba(255, 255, 255, 0.5);
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.25s ease;
    white-space: nowrap;
    position: relative;
  }

  .mini-tab:hover {
    color: rgba(255, 255, 255, 0.8);
  }

  .mini-tab.active {
    color: #FBCD01;
    border-bottom-color: #FBCD01;
    font-weight: 600;
  }

  /* Stripe-style vertical sections layout */
  .search-results-sections {
    max-width: 100%;
  }

  .search-section {
    margin-bottom: 0rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .search-section:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .search-empty-state {
    padding-top: 1rem;
  }

  .empty-message {
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .section-header {
    padding: 0.25rem 0.5rem;
    margin-bottom: 0.25rem;
  }

  .section-title {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
  }

  .section-count {
    font-size: 0.6875rem;
    opacity: 0.6;
  }

  .section-results {
    margin-top: 0;
  }

  .result-item {
    margin-bottom: 1px;
  }

  .result-link {
    display: block;
    text-decoration: none;
    color: inherit;
    border-radius: 4px;
    transition: background-color 0.12s ease;
  }

  .result-link:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }

  .result-content {
    padding: 0.375rem 0.5rem;
    gap: 0.625rem;
  }

  .result-thumbnail {
    width: 36px;
    height: 36px;
    object-fit: cover;
    flex-shrink: 0;
    border-radius: 3px;
  }

  .result-info {
    min-width: 0;
    flex: 1;
    margin-left: 0.625rem;
  }

  .result-name {
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
  }

  .result-location {
    font-size: 0.75rem;
    line-height: 1.3;
    opacity: 0.65;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
    max-width: 45%;
    text-align: right;
  }

  .result-stats {
    font-size: 0.75rem;
    line-height: 1.3;
    margin-top: 1px;
  }

  .stat-separator {
    margin: 0 0.3rem;
    opacity: 0.6;
  }

  /* Vertical layout for Hike results (popular/nearby and My Planned Hikes):
     title → short address → info row (rating · difficulty · distance),
     mirroring the Android `SearchResultCard` and the iOS `SearchResultRowView`.
     The remaining sections (Recordings, POIs) keep their right-aligned
     location chip. */
  .result-info--stacked .result-name {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }

  .result-location--block {
    display: block;
    max-width: none;
    text-align: left;
    margin-top: 1px;
    font-size: 0.6875rem; /* 1px smaller than the shared .result-location */
  }

  .result-info-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;
    font-size: 0.6875rem; /* matches the address line above */
    line-height: 1.3;
    margin-top: 2px;
    color: rgba(255, 255, 255, 0.55);
  }

  .info-row-star {
    width: 10px;
    height: 10px;
    fill: currentColor;
    margin-right: 3px;
  }

  .info-row-count {
    margin-left: 3px;
  }

  .info-row-sep {
    margin: 0 0.35rem;
    opacity: 0.55;
  }

  /* Hide the leading separator when the field it would precede is the first
     visible item in the row (e.g. rating is absent → difficulty comes first). */
  .info-row-sep--first {
    display: none;
  }

  .view-all-btn {
    color: #FBCD01 !important;
    font-weight: 500;
    font-size: 0.8125rem;
    text-decoration: none;
    padding: 0.375rem 0.5rem;
  }

  .view-all-btn:hover {
    color: #FDD935 !important;
    text-decoration: none;
    background-color: rgba(251, 205, 1, 0.08);
    border-radius: 4px;
  }

  .show-more-container {
    display: flex;
    justify-content: center;
    padding: 0.5rem 0;
    margin-top: 0.25rem;
  }

  .show-more-btn {
    color: rgba(255, 255, 255, 0.7) !important;
    font-weight: 500;
    font-size: 0.8125rem;
    text-decoration: none;
    padding: 0.5rem 1rem;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    transition: all 0.2s ease;
  }

  .show-more-btn:hover {
    color: #FBCD01 !important;
    text-decoration: none;
    background-color: rgba(251, 205, 1, 0.08);
    border-color: rgba(251, 205, 1, 0.3);
  }

  /* Legacy styles for non-logged-in view */
  .compact-list strong {
    font-weight: 600;
  }

  @media (max-width: 576px) {
    .result-thumbnail {
      width: 32px;
      height: 32px;
    }

    .result-name {
      font-size: 0.8125rem;
    }

    .result-location {
      font-size: 0.6875rem;
      max-width: 40%;
    }

    .result-stats {
      font-size: 0.6875rem;
    }

    .result-info {
      margin-left: 0.5rem;
    }

    .result-content {
      padding: 0.3rem 0.4rem;
    }

    .section-header {
      padding: 0.25rem 0.4rem;
    }
  }

  @media (max-width: 400px) {
    .result-location {
      display: none;
    }

    .result-name {
      max-width: 100%;
    }
  }

  /* Spinner animation for loading state */
  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
